export type BrowserPushStatus = 'granted' | 'default' | 'denied' | 'install-required' | 'unsupported' | 'not-configured';
export interface PushPermissionResult { granted: boolean; token?: string; status?: BrowserPushStatus }

function configuration() {
  try {
    const config = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG || '{}');
    if (!config.apiKey || !config.appId || !config.messagingSenderId || !config.projectId || !import.meta.env.VITE_FIREBASE_VAPID_KEY) return null;
    return config;
  } catch { return null; }
}

export function browserPushStatus(): BrowserPushStatus {
  if (typeof window === 'undefined' || !window.isSecureContext) return 'unsupported';
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const installed = window.matchMedia('(display-mode: standalone)').matches || Boolean((navigator as Navigator & {standalone?: boolean}).standalone);
  if (ios && !installed) return 'install-required';
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (!configuration()) return 'not-configured';
  return Notification.permission;
}

export function notificationPermissionHint(status: string): string | null {
  switch (status) {
    case 'install-required': return 'On iPhone or iPad, add Lift & Learn to your Home Screen, open it there, then save your notification preferences to enable alerts.';
    case 'not-configured': return 'Browser notifications are not configured yet. Your notification choices are saved.';
    case 'unsupported': case 'unavailable': return 'Notifications are not supported in this browser. Your notification choices are saved.';
    case 'denied': return 'Notifications are blocked. Enable them in your browser’s site settings, or in iPhone Settings → Notifications for the installed Lift & Learn web app, then return and save your preferences.';
    case 'default': return 'Save your notification preferences to enable alerts on this device.';
    default: return null;
  }
}

async function messaging() {
  const config = configuration();
  if (!config) throw new Error('Browser notifications are not configured.');
  const [{getApps, initializeApp}, sdk] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
  if (!await sdk.isSupported()) throw new Error('This browser does not support push notifications.');
  const app = getApps()[0] || initializeApp(config);
  return { instance: sdk.getMessaging(app), sdk, config };
}

async function registration(config: Record<string, string>) {
  // Firebase web config is public, not a service-account credential. Encoding it
  // in the worker URL makes initialization survive worker restarts without races.
  const url = new URL('/firebase-messaging-sw.js', window.location.origin);
  url.searchParams.set('config', JSON.stringify(config));
  const registered = await navigator.serviceWorker.register(url, {scope: '/', updateViaCache: 'none'});
  if (registered.active) return registered;
  await new Promise<void>((resolve, reject) => {
    const worker = registered.installing || registered.waiting;
    if (!worker) { reject(new Error('Notification worker did not install.')); return; }
    const timer = setTimeout(() => { worker.removeEventListener('statechange', changed); reject(new Error('Notification setup timed out. Try again.')); }, 15_000);
    const changed = () => {
      if (worker.state === 'activated') { clearTimeout(timer); worker.removeEventListener('statechange', changed); resolve(); }
      else if (worker.state === 'redundant') { clearTimeout(timer); worker.removeEventListener('statechange', changed); reject(new Error('Notification worker could not activate.')); }
    };
    worker.addEventListener('statechange', changed); changed();
  });
  return registered;
}

// Called directly from the explicit button action: requestPermission happens
// BEFORE any network operation or dynamic import, preserving Safari's gesture.
export function requestBrowserNotifications(): Promise<PushPermissionResult> {
  const status = browserPushStatus();
  if (status !== 'default' && status !== 'granted') return Promise.resolve({granted: false, status});
  const permission = Notification.permission === 'granted' ? Promise.resolve('granted' as const) : Notification.requestPermission();
  return permission.then(async result => {
    if (result !== 'granted') return {granted: false, status: result};
    const {instance, sdk, config} = await messaging();
    const serviceWorkerRegistration = await registration(config);
    // Existing server sender stores FCM tokens. Do not switch to the newer FID
    // registration API until the backend targeting contract migrates with it.
    const token = await sdk.getToken(instance, {vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY, serviceWorkerRegistration});
    localStorage.setItem('ll:browser-push-token', token);
    return {granted: true, token, status: 'granted' as const};
  }).finally(() => window.dispatchEvent(new Event('ll:notification-permission-change')));
}

export async function watchBrowserPush(handlers: {
  onForeground: (payload: {title: string; body: string; data: Record<string, unknown>}) => void;
  onClick: (data: Record<string, unknown>) => void;
  onToken?: (token: string) => void;
}): Promise<() => void> {
  if (browserPushStatus() !== 'granted') return () => {};
  const {instance, sdk} = await messaging();
  const message = (event: MessageEvent) => {
    if (event.data?.type !== 'lift-notification-click') return;
    const source = event.source as ServiceWorker | null;
    if (source?.scriptURL && new URL(source.scriptURL).origin === window.location.origin) handlers.onClick(event.data.data || {});
  };
  navigator.serviceWorker.addEventListener('message', message);
  const stop = sdk.onMessage(instance, payload => handlers.onForeground({title: payload.notification?.title || 'Lift & Learn', body: payload.notification?.body || '', data: payload.data || {}}));
  // Refresh already-granted registration only; this never asks permission.
  try {
    const result = await requestBrowserNotifications();
    if (result.token) handlers.onToken?.(result.token);
  } catch (error) {
    stop(); navigator.serviceWorker.removeEventListener('message', message); throw error;
  }
  return () => { stop(); navigator.serviceWorker.removeEventListener('message', message); };
}


export function currentBrowserPushToken(): string | null {
  return typeof localStorage === 'undefined' ? null : localStorage.getItem('ll:browser-push-token');
}

// Call during sign-out, after deactivating the saved token for the old user.
export async function clearBrowserPushToken(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (configuration()) {
    const {instance, sdk} = await messaging();
    await sdk.deleteToken(instance);
  } else {
    // Even if a later build lacks Firebase config, stop this device's old push
    // subscription so a signed-out user does not receive another account's alerts.
    const existing = await navigator.serviceWorker.getRegistration('/');
    await (await existing?.pushManager.getSubscription())?.unsubscribe();
  }
  localStorage.removeItem('ll:browser-push-token');
}
